import type {GameArm} from "games/gameArm";

/**
 * What the recipient acquisition surface SAYS happened, separate from anyone's decision to record it
 * (ui#1973).
 *
 * Owner directive, 2026-08-27: business logic emits events, telemetry subscribes. So the download page
 * and the composer name what happened and move on; `./recipientFunnelBeacon` is the only subscriber that
 * sends anything, and neither surface knows it exists. That split matters more here than usual, because
 * the natural place to put a "shown" call is inside `download/acquisitionInvite.ts` — the rule that just
 * decided the page carries an offer — and a rule that reported its own verdicts could not be asked twice
 * without double-counting.
 *
 * React-free by mandate (`logic-outside-react`): this is a plain module, and `./useRecipientOfferEvents`
 * is the adapter that wires the chip to it.
 *
 * ── The four counters are ONE funnel, and the order is the measurement ───────────────────────────
 * A recipient is the only person who meets this product without choosing it, so the download page is
 * where acquisition either happens or does not. The gap between each step is the number worth having:
 * shown to opened says whether the chip's own label earns a look, opened to claimed says whether the
 * offer inside it survives being read, and the send says whether a recipient who signed up ever became
 * a sender. Only the totals were countable before this, and a total cannot tell a surface that converts
 * from one that merely decorates.
 *
 * The signup between `recipient.offer.claimed` and `recipient.send.first` is deliberately NOT here. It
 * is already counted, once, by the consent-free signup-channel header's recipient buckets — the ones
 * `RECIPIENT_FUNNEL_CHANNELS` in `./recipientArrival` names, rather than a list repeated here, because
 * that list has now grown twice (ui#2253 split `request-page` out, ui#2963 added the two transfer-games
 * arms) and a copy of it here was stale both times (`attribution/signupChannelHeader.ts`, api#1003) —
 * counted inside the api's account-establishment
 * fence, where a replayed or rolled-back signup counts nothing. A second client-reported signup counter
 * would be a worse measurement of the same thing and would disagree with the first.
 *
 * ── Consent-free by construction, not by promise ─────────────────────────────────────────────────
 * An event is a WORD from the closed list below and nothing else. No transfer id, no capability, no
 * referral code, no path, no referrer, no session identifier, no client timestamp. There is nowhere in
 * the shape to put one: {@link RecipientFunnelEvent} has a single field, and it is typed to the
 * vocabulary. That is what lets the anonymous transport publish this for a recipient who has consented
 * to nothing, and it stops being true the moment a second field is added.
 */

/**
 * The counters this surface publishes.
 *
 * Dotted lower-case names, matching the anonymous personalization family this shares a transport with
 * rather than the upper-case spelling of the AUTHENTICATED offer family. The two spellings are not a
 * style choice: they mark which endpoint a name belongs to, and a name posted to the wrong one is parsed
 * by nothing and counted as nothing.
 *
 * These are a WIRE CONTRACT. A renamed value silently empties whatever panel reads it, so the api half
 * pins these literals rather than deriving them.
 */
export type RecipientFunnelCounter =
	| "recipient.offer.shown"
	| "recipient.offer.opened"
	| "recipient.offer.claimed"
	| "recipient.send.first"
	| RecipientArcadeCounter;

/**
 * The recipient arcade's A/B test (kitecorp/transferify-ui#2889, api half
 * kitecorp/transferify-api-service#2165): the two groups' page loads (the denominators), the game group's
 * own steps, and downloads started and finished per group, which is how the test proves the game costs no
 * download. Still one word each: the group is a coin tossed for the page load, not a fact about the person.
 */
export type RecipientArcadeCounter =
	| "recipient.arm.game"
	| "recipient.arm.control"
	| "recipient.game.offered"
	| "recipient.game.played"
	| "recipient.game.finish.shown"
	| "recipient.game.finish.send"
	| "recipient.game.finish.signup"
	| "recipient.download.started.game"
	| "recipient.download.started.control"
	| "recipient.download.finished.game"
	| "recipient.download.finished.control";

/** Records rather than string templates, so each wire word is written out where it can be searched for. */
const ARM_COUNTERS: Record<GameArm, RecipientArcadeCounter> = {
	game: "recipient.arm.game",
	control: "recipient.arm.control",
};
const DOWNLOAD_STARTED_COUNTERS: Record<GameArm, RecipientArcadeCounter> = {
	game: "recipient.download.started.game",
	control: "recipient.download.started.control",
};
const DOWNLOAD_FINISHED_COUNTERS: Record<GameArm, RecipientArcadeCounter> = {
	game: "recipient.download.finished.game",
	control: "recipient.download.finished.control",
};

/** One thing that happened on the recipient funnel. One field, for the reason the module header gives. */
export interface RecipientFunnelEvent {
	counter: RecipientFunnelCounter;
}

export type RecipientFunnelListener = (event: RecipientFunnelEvent) => void;

export type Unsubscribe = () => void;

/** The seam the recipient surfaces hold: the things they can say, and a way for others to listen. */
export interface RecipientFunnelEmitter {
	/**
	 * The gold referral chip was rendered — the funnel's denominator.
	 *
	 * <b>Callers must have already deduplicated.</b> This emitter counts what it is told, so a surface
	 * naming this on every render would publish a denominator made of renders: the download page
	 * re-renders on every progress tick of an in-flight transfer, which would inflate this counter by a
	 * factor that varies with the size of the files. {@link createOfferShownLatch} is what makes "once
	 * per surface instance" true, and `./useRecipientOfferEvents` holds one per mounted chip so no call
	 * site has to remember.
	 */
	offerShown(): void;
	/**
	 * The chip was pressed and its popover opened.
	 *
	 * Deliberately NOT deduplicated: a recipient who opens the offer, dismisses it and opens it again
	 * looked twice, and both looks are real behaviour worth seeing.
	 */
	offerOpened(): void;
	/** The claim CTA inside the popover was followed. Not deduplicated, for the same reason. */
	offerClaimed(): void;
	/**
	 * A send began in a document that arrived from the recipient funnel — at most once per document.
	 *
	 * The "at most once" is the arrival flag's doing, not this emitter's: see `./recipientArrival`.
	 */
	firstSend(): void;
	/** A download page of this document was opened in `arm`. Callers send it once per page load. */
	arm(arm: GameArm): void;
	/** Play was first shown on this page load. Callers send it once. */
	gameOffered(): void;
	/** Play was pressed. Every press. */
	gamePlayed(): void;
	/** The finish screen appeared: the download completed while the game was open. */
	finishShown(): void;
	/** "Send your own files" was pressed on the finish screen. */
	finishSend(): void;
	/** The finish screen's account button was pressed. */
	finishSignup(): void;
	/**
	 * A download the page can see started, in `arm`. A hosted one starts when its save dialog opens, so a
	 * dismissed dialog counts as a start; both groups share that, so the comparison between them holds.
	 */
	downloadStarted(arm: GameArm): void;
	/** A download the page saw through to its last byte finished, in `arm`. */
	downloadFinished(arm: GameArm): void;
	subscribe(listener: RecipientFunnelListener): Unsubscribe;
}

/**
 * A module singleton, like the personalization and offer emitters and unlike the per-page download one.
 *
 * The subject spans surfaces that are never mounted together — the download card's chip, and the
 * composer in whatever document the recipient ends up sending from — and one recipient's funnel runs
 * across both. A per-component emitter would end at each unmount and split one journey into two, which
 * is precisely the number this measurement exists to produce.
 */
export function createRecipientFunnelEmitter(): RecipientFunnelEmitter {
	const listeners = new Set<RecipientFunnelListener>();

	function emit(counter: RecipientFunnelCounter): void {
		// Iterated over a copy so a listener may unsubscribe from inside its own callback without
		// skipping the listener that follows it.
		[...listeners].forEach(listener => listener({counter}));
	}

	return {
		offerShown: () => emit("recipient.offer.shown"),
		offerOpened: () => emit("recipient.offer.opened"),
		offerClaimed: () => emit("recipient.offer.claimed"),
		firstSend: () => emit("recipient.send.first"),
		arm: arm => emit(ARM_COUNTERS[arm]),
		gameOffered: () => emit("recipient.game.offered"),
		gamePlayed: () => emit("recipient.game.played"),
		finishShown: () => emit("recipient.game.finish.shown"),
		finishSend: () => emit("recipient.game.finish.send"),
		finishSignup: () => emit("recipient.game.finish.signup"),
		downloadStarted: arm => emit(DOWNLOAD_STARTED_COUNTERS[arm]),
		downloadFinished: arm => emit(DOWNLOAD_FINISHED_COUNTERS[arm]),
		subscribe(listener) {
			listeners.add(listener);
			return () => {
				listeners.delete(listener);
			};
		},
	};
}

/** The app's emitter. One per document, for the reason {@link createRecipientFunnelEmitter} gives. */
export const recipientFunnelEvents: RecipientFunnelEmitter = createRecipientFunnelEmitter();

/**
 * The "once per chip instance, never once per render" latch.
 *
 * The unit is the surface INSTANCE, deliberately. A recipient who navigates away from a download page
 * and back has been shown the offer twice, so a module-level latch would undercount a chip that really
 * does get seen again; the latch therefore lives with the mounted surface, which is why this is a
 * factory rather than a singleton.
 *
 * One boolean rather than the offer family's per-moment set, because this surface has exactly one thing
 * to report — a set keyed by a vocabulary of one would be a Set that can only ever hold one member.
 */
export interface OfferShownLatch {
	/** True the FIRST time this instance is asked, false every time after. */
	shouldReport(visible?: boolean): boolean;
}

export function createOfferShownLatch(): OfferShownLatch {
	let reported = false;
	return {
		shouldReport(visible = true) {
			if (!visible || reported) {
				return false;
			}
			reported = true;
			return true;
		},
	};
}
